import dayjs from "dayjs";
import { json2csv } from "json-2-csv";
import { useCallback, useMemo, useState } from "react";

import { queryClient } from "@providers/QueryClient/queryClient";

import { formatExactDateTime } from "@utils/datetime";

import { useEmployeeResourceGroupInteractionsQuery } from "../EmployeeResourceGroupsTable.g";

type Props = {
  clientEmployeeId: string;
  exportType?: "csv";
  // Filter parameters - same as the table component
  parentCategoryId?: string;
  applicationResourceId?: string;
  dateRange?: [Date | null, Date | null];
};

export const useEmployeeResourceGroupsTableExport = ({
  clientEmployeeId,
  exportType = "csv",
  parentCategoryId,
  applicationResourceId,
  dateRange,
}: Props) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportData = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      // Convert date range to Date strings (YYYY-MM-DD format)
      const dateFrom = dateRange?.[0]
        ? dayjs(dateRange[0]).format("YYYY-MM-DD")
        : undefined;
      const dateTo = dateRange?.[1]
        ? dayjs(dateRange[1]).format("YYYY-MM-DD")
        : undefined;

      // First, fetch to get the total count
      const initialData = await queryClient.fetchQuery({
        queryKey: useEmployeeResourceGroupInteractionsQuery.getKey({
          employeeId: clientEmployeeId,
          dateFrom,
          dateTo,
          ...(parentCategoryId && { categoryIds: [parentCategoryId] }),
          ...(applicationResourceId && { applicationResourceId }),
          offset: 0,
          limit: 100,
        }),
        queryFn: useEmployeeResourceGroupInteractionsQuery.fetcher({
          employeeId: clientEmployeeId,
          dateFrom,
          dateTo,
          ...(parentCategoryId && { categoryIds: [parentCategoryId] }),
          ...(applicationResourceId && { applicationResourceId }),
          offset: 0,
          limit: 100,
        }),
      });

      const totalCount =
        initialData?.clientEmployeeApplicationInteractions?.filteredCount || 0;

      if (totalCount === 0) {
        throw new Error("No resource groups found for this employee");
      }

      // Fetch all data in batches
      const allInteractions = [];
      const batchSize = 100;
      let offset = 0;

      // Add initial batch
      if (initialData?.clientEmployeeApplicationInteractions?.interactions) {
        allInteractions.push(
          ...initialData.clientEmployeeApplicationInteractions.interactions
        );
        offset = batchSize;
      }

      // Fetch remaining batches
      while (offset < totalCount) {
        const batchData = await queryClient.fetchQuery({
          queryKey: useEmployeeResourceGroupInteractionsQuery.getKey({
            employeeId: clientEmployeeId,
            dateFrom,
            dateTo,
            ...(parentCategoryId && { categoryIds: [parentCategoryId] }),
            ...(applicationResourceId && { applicationResourceId }),
            offset,
            limit: batchSize,
          }),
          queryFn: useEmployeeResourceGroupInteractionsQuery.fetcher({
            employeeId: clientEmployeeId,
            dateFrom,
            dateTo,
            ...(parentCategoryId && { categoryIds: [parentCategoryId] }),
            ...(applicationResourceId && { applicationResourceId }),
            offset,
            limit: batchSize,
          }),
        });

        if (batchData?.clientEmployeeApplicationInteractions?.interactions) {
          allInteractions.push(
            ...batchData.clientEmployeeApplicationInteractions.interactions
          );
        }

        offset += batchSize;
      }

      if (!allInteractions || allInteractions.length === 0) {
        throw new Error("No resource groups found for this employee");
      }

      // Map interactions to export format - matching table column structure exactly
      const exportDataArray = allInteractions.map((interaction) => {
        const primaryResource = interaction.primaryResource;
        const publicResource = primaryResource.publicResource;
        const category = publicResource?.category;

        return {
          Application: primaryResource.productName || "",
          Vendor: primaryResource.vendorName || "",
          Description: primaryResource.productDescription || "",
          Category: category?.parent?.name || category?.name || "",
          "Last accessed":
            formatExactDateTime(interaction.lastInteractionAt) ?? "",
        };
      });

      // Sort by Application name
      exportDataArray.sort((a, b) =>
        a.Application.localeCompare(b.Application)
      );

      if (!exportDataArray || exportDataArray.length === 0) {
        throw new Error("No resource groups found");
      }

      if (exportType === "csv") {
        const csv = json2csv(exportDataArray);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `employee-resource-groups-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export failed:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [
    exportType,
    clientEmployeeId,
    parentCategoryId,
    applicationResourceId,
    dateRange,
    isExporting,
  ]);

  return useMemo(() => ({ exportData, isExporting }), [exportData, isExporting]);
};